import { ChangeDetectorRef, Component, ElementRef, Input, OnChanges, SimpleChanges } from '@angular/core';
import { ButtonInfo } from '../../../common/UI-Components/helperClasses/value-and-display.class';
import { Store } from '@ngxs/store';
import { SetOpenedPanel } from '../../../Store/app.state/app.actions';
import { APPLICATION_MODE, IAppConf, SCENE_MODE } from '../../../Store/app.state/app.model';
import { DiscussionApiSvc } from '../../../services/api.services/discussion.api.svc';
import { AppState } from '../../../Store/app.state/app.state';
import { Observable } from 'rxjs';
import { SetSelectedPlacemark } from '../../../Store/discussions.state/discussions.actions';
import { Actions, PermissionsManager } from '../../../services/permissions-manager';
import { PointCloudSvc } from 'src/app/services/api.services/point-cloud.service';
import { Router } from '@angular/router';
import { RoleManagementNavService } from '../../../role-management/services/role-management-nav.service';

import { MatIcon } from '@angular/material/icon';

enum IntositeSidebarOption {
  BACK = 'sidebar.back',
  HOME = 'sidebar.home',
  SEARCH = 'sidebar.search',
  SITES = 'sidebar.sites',
  ZONES = 'sidebar.zones',
  LAYERS = 'sidebar.layers',
  VIEWPOINTS = 'sidebar.viewpoints',
  DISCUSSION = 'sidebar.discussion'
}

enum TourSidebarOption {
  PLACEMARKS = 'sidebar.tour.placemarks',
}

enum PanelOption {
  ADMIN = 'panels.admin',
  HELP = 'panels.help',
  SETTINGS = 'panels.settings',
  PROFILE = 'panels.profile',
}

export const ModeDependentPanels: string[] = [IntositeSidebarOption.SEARCH, IntositeSidebarOption.SITES, IntositeSidebarOption.DISCUSSION];

@Component({
    selector: 'ins-side-bar-container',
    templateUrl: './side-bar.container.html',
    styleUrls: ['./side-bar.container.scss'],
    imports: [MatIcon]
})
export class SideBarContainer implements OnChanges {

  pressedPanelName$: Observable<string>;

  public upperButtonsInfo: ButtonInfo[] = [];
  public upperNavButtonsInfo: ButtonInfo[] = [];
  public lowerButtonsInfo: ButtonInfo[] = [];
  public userButtonInfo: ButtonInfo;
  public userButtonIcon: string;
  public isEmbeddedMode = false;
  private hiddenPanels: string[] = [];

  @Input() public sceneMode: SCENE_MODE;
  @Input() public pressedMenuName: string = '';
  @Input() public currentPage: string = '';

  appConf$: Observable<IAppConf>;
  constructor(private readonly store: Store, public _elementRef: ElementRef, private readonly discussionApiSvc: DiscussionApiSvc,
              private readonly changeDetectorRef: ChangeDetectorRef, private readonly pointCloudSvc: PointCloudSvc, private readonly router: Router,
              private readonly roleNavService: RoleManagementNavService) {
    this.pressedPanelName$ = this.store.select(AppState.getOpenedPanel);
    this.appConf$ = this.store.select(AppState.getAppConf);
    this.appConf$.subscribe((appConf: IAppConf) => {
      this.isEmbeddedMode = appConf?.appMode === APPLICATION_MODE.EMBEDDED;
      if (appConf?.appMode === APPLICATION_MODE.EMBEDDED) {
        this.userButtonIcon = 'panels.profile.light';
      } else {
        this.userButtonIcon = 'panels.profile';
      }
    });

    this.pointCloudSvc.isPointCloudViewerDisplayed$.subscribe((isDisplayed: boolean) => {
      if (isDisplayed) {
        this.hiddenPanels.push(...Object.values(IntositeSidebarOption));
        this.hiddenPanels.push(...Object.values(PanelOption));
      } else {
        this.hiddenPanels = [];
      }
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['currentPage']?.currentValue) {
      this.loadButtonsAsPerPage(this.currentPage);
    }
  }

  private loadButtonsAsPerPage(currentPage: string): void {
    this.upperButtonsInfo = [];
    this.upperNavButtonsInfo = [];
    this.lowerButtonsInfo = [];
    this.hiddenPanels = [];

    switch (currentPage) {
      case '/':
        this.loadIntositeButtons();
        break;
      case '/Tour':
        this.loadTourButtons();
        break;
      case '/usermanagement':
        this.loadUserManagementButtons();
        break;
      case '/rolemanagement':
        this.loadRoleManagementButtons();
        break;
        default:

    }

  };

  private loadIntositeButtons(): void {
    this.loadUpperButtonsAsPerPermissions();
    this.loadLowerButtonsAsPerPermissions();
    this.userButtonInfo = new ButtonInfo(PanelOption.PROFILE, '');
  }

  private loadTourButtons(): void {
    this.lowerButtonsInfo.push(
      new ButtonInfo(PanelOption.HELP, 'Help'),
    );
    this.upperButtonsInfo.push(
      new ButtonInfo(TourSidebarOption.PLACEMARKS, 'Placemarks'),
    );

    if (!window.location.search.includes("type=public")) {
      this.userButtonInfo = new ButtonInfo(PanelOption.PROFILE, '');
    }
  }

  private loadUserManagementButtons(): void {
    this.lowerButtonsInfo.push(
      new ButtonInfo(PanelOption.HELP, 'Help'),
    );
    this.userButtonInfo = new ButtonInfo(PanelOption.PROFILE, '');
  }

  private loadRoleManagementButtons(): void {
    this.upperNavButtonsInfo.push(
      new ButtonInfo(IntositeSidebarOption.HOME, 'Home'),
      new ButtonInfo(IntositeSidebarOption.BACK, 'Back')
    );

    this.lowerButtonsInfo.push(
      new ButtonInfo(PanelOption.HELP, 'Help'),
    );
    this.userButtonInfo = new ButtonInfo(PanelOption.PROFILE, '');
  }

  private loadUpperButtonsAsPerPermissions(): void {
    this.upperButtonsInfo.push(
      new ButtonInfo(IntositeSidebarOption.SEARCH, 'Search'),
      new ButtonInfo(IntositeSidebarOption.SITES, 'Sites'),
      new ButtonInfo(IntositeSidebarOption.ZONES, 'Zones'),
      new ButtonInfo(IntositeSidebarOption.LAYERS, 'Layers'),
      new ButtonInfo(IntositeSidebarOption.VIEWPOINTS, 'Viewpoints'),
      new ButtonInfo(IntositeSidebarOption.DISCUSSION, 'Discussion')
    );
  }

  private loadLowerButtonsAsPerPermissions(): void {
    PermissionsManager.isPermitted$(Actions.ADMIN_MENU_BUTTON).subscribe((isPerm: boolean) => {
      const adminButton: ButtonInfo = new ButtonInfo(PanelOption.ADMIN, 'Admin');
      if (isPerm) {
        if (!this.lowerButtonsInfo) {
          this.lowerButtonsInfo.push(adminButton);
        } else if (!this.lowerButtonsInfo.find((button: ButtonInfo) => { return adminButton.displayValue === button.displayValue; })) {
          this.lowerButtonsInfo.unshift(adminButton); // Add element in the first place
          this.changeDetectorRef.detectChanges();
        }
      } else if (this.lowerButtonsInfo.find((button: ButtonInfo) => { return adminButton.displayValue === button.displayValue; })) {
        this.lowerButtonsInfo.shift(); // Remove Admin from array
        this.changeDetectorRef.detectChanges();
      }
    });

    this.lowerButtonsInfo.push(
      new ButtonInfo(PanelOption.HELP, 'Help'),
      new ButtonInfo(PanelOption.SETTINGS, 'Settings')
    );
    this.userButtonInfo = new ButtonInfo(PanelOption.PROFILE, '');
  }

  public isHidden(panelName: string): boolean {
    let isHidden = false;
    if (this.sceneMode === SCENE_MODE.Panoramic && ModeDependentPanels.find((name: string) => name === panelName)) {
      isHidden = true;
    }
    if (this.hiddenPanels.find((name: string) => name === panelName)) {
      isHidden = true;
    }
    return isHidden;
  }

  public upperNavBtnClick(menuName: string): void {
    if (menuName === IntositeSidebarOption.HOME) {
      if (this.currentPage === '/rolemanagement') {
        this.roleNavService.navigateHome();
        return;
      }
      this.router.navigate([this.currentPage]);
      return;
    }

    if (menuName === IntositeSidebarOption.BACK) {
      if (this.currentPage === '/rolemanagement') {
        this.roleNavService.navigateBack();
        return;
      }
    }
  }

  public toggleMenu(menuName: string): void {
    this.store.dispatch(new SetOpenedPanel(menuName));

    if (menuName === IntositeSidebarOption.DISCUSSION) {
      this.pressedPanelName$.subscribe((name: string) => {
        if (name === menuName) {
          this.discussionApiSvc.getAllPlacemarksWithComments(undefined);
        } else {
          this.store.dispatch(new SetSelectedPlacemark(undefined));
        }
      }).unsubscribe();
    } else {
      this.store.dispatch(new SetSelectedPlacemark(undefined));
    }
  }

}
